import * as React from "react";
import { QueryRoute } from "bernie-core";
import { Layer } from "bernie-view";
import { useLocalization } from "@shared-ui/localization-context";
import { inject } from "mobx-react";

import { UitkToolbar } from "uitk-react-toolbar";
import {
  UitkMediaGalleryImageDialog,
  UitkMediaGalleryImageView,
  UitkMediaGalleryThumbnailView,
} from "uitk-react-media-gallery";
import { UitkFullscreenDialog, UitkDialogContent, UitkDialogAnimationType } from "uitk-react-dialog";

import { GalleryCarouselProps } from "../typings";
import { getFullScreenImages } from "components/utility/GalleryImageUtil";
import { UitkGalleryCarousel, UitkGalleryCarouselProps } from "./UitkGalleryCarousel";
import { Action, FlexTrackingInfo, sendImmediateTrackEvent } from "components/utility/analytics/FlexAnalyticsUtils";

const GalleryCarousel = inject("routeStore")((props: GalleryCarouselProps) => {
  const { title, images, identifier, analyticsModule, analytics, routeStore } = props;
  const { formatText } = useLocalization();
  const galleryImages = getFullScreenImages(images);

  const [currentCarouselIndex, setCurrentCarouselIndex] = React.useState(0);
  const [currentMediaGalleryIndex, setCurrentMediaGalleryIndex] = React.useState(0);

  const mediaGalleryQuery = identifier ? { gallery: `open${identifier}` } : { gallery: "open" };

  const thumbnailsGalleryQuery = identifier ? { thumbnails: `open${identifier}` } : { thumbnails: "open" };

  const setCarouselIndex = (index: number) => {
    const trackingInfo: FlexTrackingInfo = {
      moduleName: "GalleryCarousel",
      rfrr: "carousel.gallery-nav.btn",
      action: Action.CLICK,
      linkName: "Navigating carousel gallery viewer",
    };
    sendImmediateTrackEvent(trackingInfo, analytics);

    setCurrentCarouselIndex(index);
  };

  const openMediaGallery = (index?: number) => {
    const trackingInfo: FlexTrackingInfo = {
      moduleName: "GalleryCarousel",
      rfrr: "carousel.gallery.open",
      action: Action.CLICK,
      linkName: "RFRR Action Event",
    };
    sendImmediateTrackEvent(trackingInfo, analytics);

    if (index === undefined) {
      setCurrentMediaGalleryIndex(currentCarouselIndex);
    } else {
      setCurrentMediaGalleryIndex(index);
    }

    if (routeStore) {
      routeStore.updateSearch(mediaGalleryQuery);
    }
  };

  const openThumbnailsGallery = () => {
    const trackingInfo: FlexTrackingInfo = {
      moduleName: "GalleryCarouselThumbnails",
      rfrr: "carousel.gallery.thumbnails.open",
      action: Action.CLICK,
      linkName: "RFRR Action Event",
    };
    sendImmediateTrackEvent(trackingInfo, analytics);

    if (routeStore) {
      routeStore.updateSearch(thumbnailsGalleryQuery);
    }
  };

  const closeMediaGallery = () => {
    const trackingInfo: FlexTrackingInfo = {
      moduleName: "GalleryCarousel",
      rfrr: "carousel.gallery.close",
      action: Action.CLICK,
      linkName: "RFRR Action Event",
    };
    sendImmediateTrackEvent(trackingInfo, analytics);

    if (routeStore) {
      routeStore.closeDialog("gallery");
    }
  };

  const closeThumbnailsGallery = () => {
    const trackingInfo: FlexTrackingInfo = {
      moduleName: "GalleryCarouselThumbnails",
      rfrr: "carousel.gallery.thumbnails.close",
      action: Action.CLICK,
      linkName: "RFRR Action Event",
    };
    sendImmediateTrackEvent(trackingInfo, analytics);
    if (routeStore) {
      routeStore.closeDialog("thumbnails");
    }
  };

  const galleryProps: UitkGalleryCarouselProps = {
    allyHeadingLevel: 2,
    allyHeadingText: title,
    carouselName: analyticsModule,
    images,
    currentIndex: currentCarouselIndex,
    onIndexChange: setCarouselIndex,
    onMediaButtonClick: openThumbnailsGallery,
    onImageClick: openMediaGallery,
    showGalleryButton: true,
  };

  return (
    <div className="GalleryCarousel" data-testid="galleryCarouselImage">
      <UitkGalleryCarousel {...galleryProps} />

      <QueryRoute query={mediaGalleryQuery}>
        <Layer id="destination-image-gallery" onDismiss={closeMediaGallery}>
          <UitkMediaGalleryImageDialog
            animation={UitkDialogAnimationType.NONE}
            dialogShow={true}
            strictFullscreen={true}
            onDismiss={closeMediaGallery}
          >
            <UitkMediaGalleryImageView
              images={galleryImages}
              currentIndex={currentMediaGalleryIndex}
              onIndexChange={setCurrentMediaGalleryIndex}
            />
          </UitkMediaGalleryImageDialog>
        </Layer>
      </QueryRoute>
      <QueryRoute query={thumbnailsGalleryQuery}>
        <Layer id="destination-image-thumbnails" onDismiss={closeThumbnailsGallery}>
          <UitkFullscreenDialog
            strictFullscreen={false}
            dialogShow={false}
            animation={UitkDialogAnimationType.SLIDEUP}
            className="uitk-dialog-fullscreen-bg-default"
          >
            <UitkDialogContent className="no-padding">
              <UitkToolbar
                header={title}
                iconLabel={formatText("destinationThumbnailsGallery.button.close")}
                action={closeThumbnailsGallery}
              />
              <UitkMediaGalleryThumbnailView images={images} onImageSelected={openMediaGallery} />
            </UitkDialogContent>
          </UitkFullscreenDialog>
        </Layer>
      </QueryRoute>
    </div>
  );
});

export default GalleryCarousel;
